import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {MatLegacyPaginator as MatPaginator} from '@angular/material/legacy-paginator';
import {MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {EnvService} from '../../services/env.service';

@Component({
  selector: 'micro-managed-command-log',
  templateUrl: './managedCommand-log.component.html',
  styles: [`
    .managed-command-results-table > tbody > tr > td:first-child {
      white-space: normal;
      overflow-wrap: break-word;
      text-overflow: clip;
    }
  `]
})
export class ManagedCommandLogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() managedCommand: any;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  logs: any[] = [];
  lastRun: any;
  lastFailure: any;
  lastSuccess: any;
  status = '';
  error = '';
  loading = false;
  loadingLastRun = false;
  pageLoadError = false;
  lastRunLoadError = false;

  private filterChanges = new Subject<void>();
  private filterSubscription: Subscription;
  private pageSubscription: Subscription;
  private lastRunSubscription: Subscription;
  private detailSubscription: Subscription;
  private logDialog: MatDialogRef<any>;

  constructor(private env: EnvService,
              private http: HttpClient,
              private dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.filterSubscription = this.filterChanges.pipe(debounceTime(500)).subscribe(() => this.reloadPage());
    this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.managedCommand && !changes.managedCommand.firstChange) {
      this.detailSubscription?.unsubscribe();
      this.logDialog?.close();
      this.logs = [];
      this.lastRun = undefined;
      this.lastFailure = undefined;
      this.lastSuccess = undefined;
      this.status = '';
      this.error = '';
      this.paginator.pageIndex = 0;
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.pageSubscription?.unsubscribe();
    this.lastRunSubscription?.unsubscribe();
    this.detailSubscription?.unsubscribe();
    this.logDialog?.close();
    this.filterChanges.complete();
  }

  filterChanged(): void {
    this.paginator.pageIndex = 0;
    this.filterChanges.next();
  }

  reload(): void {
    this.reloadPage();
    this.lastRunSubscription?.unsubscribe();
    this.loadingLastRun = true;
    this.lastRunLoadError = false;
    this.lastRunSubscription = this.http.get(`${this.logUrl}/last-run`, {params: this.companyParams}).subscribe({
      next: (data: any) => {
        this.lastRun = data.lastRun ? this.prepareLog(data.lastRun) : undefined;
        this.lastFailure = data.lastFailure;
        this.lastSuccess = data.lastSuccess;
        this.loadingLastRun = false;
      },
      error: () => {
        this.loadingLastRun = false;
        this.lastRunLoadError = true;
      }
    });
  }

  reloadPage(): void {
    this.pageSubscription?.unsubscribe();
    this.loading = true;
    this.pageLoadError = false;
    const params = this.companyParams
      .set('status', this.status)
      .set('error', this.error)
      .set('page', String(this.paginator.pageIndex || 0))
      .set('size', String(this.paginator.pageSize || 5));
    this.pageSubscription = this.http.get(this.logUrl, {params}).subscribe({
      next: (page: any) => {
        this.logs = page.content;
        this.paginator.pageIndex = page.number;
        this.paginator.pageSize = page.size;
        this.paginator.length = page.totalElements;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.pageLoadError = true;
      }
    });
  }

  showLog(id: string, template: TemplateRef<any>): void {
    this.detailSubscription?.unsubscribe();
    this.detailSubscription = this.http.get(`${this.logUrl}/${id}`, {params: this.companyParams}).subscribe(data => {
      this.logDialog?.close();
      this.logDialog = this.dialog.open(template, {
        data: this.prepareLog(data),
        width: '1000px',
        maxWidth: '95vw'
      });
    });
  }

  isStructured(value: any): boolean {
    return value !== null && typeof value === 'object';
  }

  private get logUrl(): string {
    return `${this.env.e.url}/cmd/managed/${this.managedCommand.id}/log`;
  }

  private get companyParams(): HttpParams {
    return new HttpParams().set('companyId', this.managedCommand.companyId || '');
  }

  private prepareLog(log: any): any {
    return {...log, cmdRsp: this.parseResponse(log.cmdRsp), scriptRsp: this.parseResponse(log.scriptRsp)};
  }

  private parseResponse(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
}
